<template>
  <div class="t-profile-child__container t-profile__box">
    <h2 class="t-profile-child__title">{{ $root.staticText.lk.securityTitle }}</h2>
    <form class="t-profile-child__form t-form" @submit.stop.prevent="submit()">
      <div class="t-form__item _half-lk" v-for="field in form" :key="field.name">
        <div v-if="field.label" class="t-form__label">
          {{ field.label }}
          <span v-if="field.required">*</span>
        </div>

        <LkInput
          :placeholder="field.hintField"
          v-model="field.value"
          @change="field.debounceChange"
          @blur="handleFieldValidate(field)"
          :type="showPassword ? 'text' : 'password'"
          :error="field.error"
          :valid="field.valid"
          :name="field.name"
        >
          <template #action>
            <LkToggleBtn v-model="showPassword">
              <template #start-state><EyeClosedSvg /></template>
              <template #end-state><EyeSvg /></template>
            </LkToggleBtn>
          </template>
        </LkInput>

        <p v-if="field.error" class="t-form__error">
          {{ field.error }}
        </p>
      </div>

      <div class="t-profile-child__text">{{ $root.staticText.lk.securityText }}</div>
      <div class="t-profile-child__block">
        {{ $root.staticText.lk.securitySubtext }}
        <a href="https://online.axoftglobal.ru/purchases/requests/all-requests" target="_blank" class="t-profile-child__block-link">
          {{ $root.staticText.lk.securitySubtext2 }}
        </a>
        <a :href="'mailto:' + $parent.securityEmail" class="t-profile-child__block-link">
          {{ $parent.securityEmail }}
        </a>
      </div>

      <vue-recaptcha
        ref="recaptcha"
        size="invisible"
        :sitekey="$root.sitekeyRecaptcha"
        :load-recaptcha-script="true"
        @verify="onCapchaRegister"
        @expired="onCaptchaExpired"
        @error="errorCaptcha"
      />
      <button class="t-btn _lk _green t-profile-child__btn t-form__btn" :class="{ _disabled: disabledBtn }">
        {{ $root.staticText.lk.securityBtn }}
      </button>
    </form>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import Vue from 'vue'
import VueRecaptcha from 'vue-recaptcha'
import LkInput from '@components/lk/lk-input'
import LkToggleBtn from '@components/lk/lk-toggle-btn'
import EyeSvg from '@/public/dist/img/svg/Eye.svg?inline'
import EyeClosedSvg from '@/public/dist/img/svg/EyeClosed.svg?inline'
import debounce from 'lodash/debounce'
import { ERROR_MESSAGES, PWD_RGX } from '@pages/lk/profile/security/const'

export default {
  name: 'security',
  components: {
    VueRecaptcha,
    LkInput,
    LkToggleBtn,
    EyeSvg,
    EyeClosedSvg,
  },
  data() {
    return {
      recaptchaToken: null,
      isValidForm: true,
      showPassword: false,
      form: {
        password: {
          label: this.$root.staticText.lk.password,
          name: 'password',
          type: 'password',
          value: '',
          hintField: this.$root.staticText.lk.placeholderPassword,
          error: null,
          valid: false,
          required: true,
          pattern: PWD_RGX,
          debounceChange: this.debouncePwdInput,
          validate: field => {
            const { pattern, value, name } = field

            const isValid = pattern.test(value)

            if (isValid) {
              this.setFieldState(name, isValid)
              if (this.form.passwordRepeat.value) this.handleFieldValidate(this.form.passwordRepeat)
            } else {
              this.setFieldState(name, isValid, ERROR_MESSAGES.notMatchPwd)
            }
          },
        },
        passwordRepeat: {
          label: this.$root.staticText.lk.passwordRepeat,
          name: 'passwordRepeat',
          type: 'password',
          value: '',
          hintField: this.$root.staticText.lk.placeholderPasswordRepeat,
          error: false,
          valid: false,
          required: true,
          pattern: PWD_RGX,
          debounceChange: this.debounceRePwdInput,
          validate: field => {
            const { pattern, value, name } = field

            const password = this.form.password

            if (password.value !== value) {
              this.setFieldState(name, false, ERROR_MESSAGES.notEqualsPwd)
              return
            }

            const isValid = pattern.test(value)

            if (isValid) this.setFieldState(name, isValid)
            else this.setFieldState(name, isValid, ERROR_MESSAGES.notMatchPwd)
          },
        },
      },
      disabledBtn: true,
    }
  },
  methods: {
    ...mapActions('modal', ['modalType_lkSuccessOpen', 'modalType_lkErrorOpen', 'modalClose', 'modalType_requestOpen']),
    onCapchaRegister(recaptchaToken) {
      this.recaptchaToken = recaptchaToken
      this.sendForm()
    },
    onCaptchaExpired() {
      this.$refs.recaptcha.reset()
    },
    errorCaptcha() {
      console.log('errorCaptcha')
    },
    debouncePwdInput: debounce(function() {
      this.handleFieldValidate(this.form.password)
    }, 700),
    debounceRePwdInput: debounce(function() {
      this.handleFieldValidate(this.form.passwordRepeat)
    }, 700),
    handleFieldValidate({ name }) {
      const field = this.form[name]

      if (field.validate) field.validate(field)
    },
    setFieldState(name, isValid, errorMessage) {
      this.form[name].valid = isValid
      this.form[name].error = isValid ? '' : errorMessage

      this.refreshFormState()
    },
    refreshFormState() {
      this.isValidForm = this.form.password.valid && this.form.passwordRepeat.valid
    },
    submit() {
      if (this.isValidForm) {
        this.$refs.recaptcha.execute()
      }
    },
    sendForm() {
      let fd = new FormData()
      fd.append('recaptchaToken', this.recaptchaToken)

      for (const name in this.form) {
        fd.append(name, this.form[name].value)
      }

      let action = process.env.NODE_ENV !== 'production' ? 'lk/logout.json' : 'lk/newPassword'
      let methods = process.env.NODE_ENV !== 'production' ? 'get' : 'post'

      this.disabledBtn = true
      Vue.http[methods](action, fd)
        .then(response => response.json())
        .then(data => {
          if (data.success) {
            this.modalType_lkSuccessOpen({
              title: this.$root.staticText.lk.successText,
              closeBthNone: true,
            })
          } else {
            this.openError(data)
          }
        })
        .catch(data => {
          this.openError(data)
        })
        .finally(() => {
          this.disabledBtn = false
        })
    },
    openError(data) {
      let text = this.$root.staticText.lk.lkErrorText
      if (data.errors && data.errors.length > 0) {
        let key = Object.keys(data.errors)[0]
        if (data.errors[key][0]) {
          text = data.errors[key][0]
        }
      }
      this.modalType_lkErrorOpen({
        title: this.$root.staticText.lk.lkErrorTitle,
        text: text,
        closeBthNone: true,
      })
    },
  },
  watch: {
    isValidForm(value) {
      this.disabledBtn = !value
    },
  },
}
</script>

<style lang="sass">
.t-form
  &__item
    vertical-align: top

  &__error
    margin-top: 6px
    color: #ec7c7c
    font-size: 12px
    line-height: normal
</style>



// WEBPACK FOOTER //
// src/templates/pages/lk/profile/security/index.vue